import { forwardRef, type JSX } from "react";
import { createPortal } from "react-dom";
import { cn } from "@repo/utils";
import { SelectInputButton } from "./select-input-button.js";
import { SelectOptions } from "./select-options.js";
import { useSelectLogic } from "./use-select-logic.js";

// eslint-disable-next-line react-refresh/only-export-components
export const variants = {
  default: "default",
  small: "small",
} as const;

export type Variant = (typeof variants)[keyof typeof variants];

export type SelectOptionValue = string | number;

export type SelectOption<T extends SelectOptionValue = string> = {
  value: T;
  label: string;
  disabled?: boolean;
  suffix?: React.ReactNode;
};

export type Props<T extends SelectOptionValue = string> = {
  options: SelectOption<T>[];
  value?: T | T[];
  onChange?: (value: T | T[] | undefined) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  multiple?: boolean;
  clearable?: boolean;
  showClearIcon?: boolean;
  enableScrollToSelected?: boolean;
  darkSelectedText?: boolean;
  className?: string;
  variant?: Variant;
  openUpward?: boolean;
};

function SelectInputInner<T extends SelectOptionValue = string>(
  {
    options,
    value,
    onChange,
    placeholder = "Select an option",
    label,
    error,
    helperText,
    disabled = false,
    required = false,
    multiple = false,
    clearable = true,
    showClearIcon = false,
    enableScrollToSelected = false,
    darkSelectedText = false,
    className,
    variant = "default",
    openUpward = false,
  }: Props<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const {
    isOpen,
    highlightedIndex,
    selectRef,
    dropdownRef,
    coords,
    selectedValues,
    selectedOptions,
    toggleOpen,
    handleClearAll,
    handleSelect,
    handleKeyDown,
  } = useSelectLogic({ options, value, onChange, disabled, multiple, clearable, openUpward });

  return (
    <div className={cn("w-full", className)} ref={ref}>
      {label && (
        <label className="mb-2 block text-sm font-medium">
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}
      <div
        ref={selectRef}
        className="relative outline-none"
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        <SelectInputButton
          disabled={disabled}
          toggleOpen={toggleOpen}
          isSelectOpen={isOpen}
          hasError={!!error}
          showClearIcon={showClearIcon}
          variant={variant}
          selectedValues={selectedValues}
          handleClearAll={handleClearAll}
          selectedOptions={selectedOptions}
          placeholder={placeholder}
          multiple={multiple}
          darkSelectedText={darkSelectedText}
        />

        {isOpen &&
          createPortal(
            <div
              ref={dropdownRef}
              style={{
                position: "absolute",
                top: coords.top,
                left: coords.left,
                width: coords.width,
                zIndex: 99999,
              }}
              className={cn(
                "animate-fade-in rounded-lg border border-gray-200 bg-white shadow-lg",
                openUpward ? "mb-1" : "mt-1"
              )}
              data-allow-navigation
            >
              <div className="max-h-60 overflow-y-auto py-1">
                <SelectOptions
                  options={options}
                  selectedValues={selectedValues}
                  handleSelect={handleSelect}
                  highlightedIndex={highlightedIndex}
                  enableScrollToSelected={enableScrollToSelected}
                />
              </div>
            </div>,
            document.body
          )}
      </div>

      {error && <p className="mt-1.5 text-sm text-red-500">{error}</p>}
      {!error && helperText && <p className="mt-1.5 text-sm text-gray-500">{helperText}</p>}
    </div>
  );
}

SelectInputInner.displayName = "SelectInput";

export const SelectInput = forwardRef(SelectInputInner) as <T extends SelectOptionValue = string>(
  props: Props<T> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => JSX.Element;
